import { supabase } from "../supabase/SupabaseClient";

/* Los pedidos al SISTEMA — lo que alguien escribe con `/ticket` por WhatsApp.
 *
 * ── TRES COSAS SE LLAMAN «TICKET» Y ÉSTA ES LA TERCERA ─────────────────────
 *
 *   · `taller_tickets`  — una reparación de un camión, en la ticketera de
 *                         FlotaBot;
 *   · `error_tickets`   — los abre el BOT solo cuando se rompe;
 *   · el área «sistema» de `public.tickets` — los abre una PERSONA y hablan
 *                         del sistema.
 *
 * Por eso el comando lleva barra: «ticket» a secas ya significaba el taller, y
 * sin ella la mitad de los pedidos se cargaría como una reparación **sin que
 * nadie se entere**, porque las dos cosas contestan «listo».
 *
 * ── ERAN DOS TABLAS Y LAS DOS SE ESCRIBÍAN ─────────────────────────────────
 *
 * Esta pantalla leía `public.pedidos` y `pedidos.py` de FlotaBot escribía en
 * `public.tickets`. **De 22 ids compartidos, 21 tenían texto distinto**: «P-14»
 * decía una cosa por WhatsApp y otra acá, y no había forma de saber cuál era el
 * pedido de verdad. No fallaba en ningún lado — las dos pantallas dibujaban una
 * lista perfectamente creíble.
 *
 * Y el daño no era sólo la numeración: `pedidos_seguimiento.barrer()` lee
 * `tickets`, así que **lo que se marcaba resuelto desde el hub no le avisaba
 * nunca a quien lo había pedido**. El circuito que P-28 vino a cerrar quedaba
 * abierto justo del lado de esta pantalla.
 *
 * Las 29 filas viejas entraron en `tickets` con
 * `20260813120000_los_pedidos_viejos_entran_en_tickets.sql`, y de acá en más
 * hay **un solo número por pedido**.
 *
 * ── ESTA PANTALLA ES EL ÁREA «SISTEMA», NO OTRA TABLA ──────────────────────
 *
 * `/tickets` muestra `public.tickets` entera —las ocho áreas— y esto es la
 * rebanada de `sistema`, que es la que llega por WhatsApp. Filtrar por área es
 * lo único que la separa: si mañana se decide que sobra, lo que hay que sacar
 * es la pantalla, no la tabla.
 */

/* Los estados son los de `pedidos.py`, que son los del CHECK de la tabla.
 *
 * `descartado` YA NO ESTÁ, y no es una simplificación: la tabla no lo acepta
 * —el CHECK son estos cinco— y `pedidos.ALIAS_ESTADO` lo traduce a `cerrado`
 * desde siempre. Ofrecerlo era un botón que la base iba a rechazar. */
export const ESTADOS = [
  { clave: "abierto", label: "Abiertos" },
  { clave: "en_curso", label: "En curso" },
  { clave: "esperando", label: "Esperando respuesta" },
  { clave: "resuelto", label: "Resueltos" },
  { clave: "cerrado", label: "Cerrados" },
];

/* Los que todavía piden algo. `cerrado` NO está: descartar no es borrar
 * —queda la constancia de que alguien decidió no hacerlo, igual que anular un
 * movimiento— pero tampoco es trabajo pendiente. */
export const ABIERTOS = ["abierto", "en_curso", "esperando"];

const AREA = "sistema";

const CAMPOS =
  "id,titulo,detalle,estado,abierto_por,abierto_por_nombre,origen,issue_url,"
  + "respuesta,triage,pr_url,creado_en,movido_en,cerrado_en";

/* CÓMO SE LEE UNA FILA, y es la MISMA regla que `pedidos._como_pedido`.
 *
 * El bot parte el pedido en `titulo` (lo que entra en 120 caracteres) y
 * `detalle` (el original entero, sólo cuando no entró), así que el texto que
 * la persona escribió es `detalle || titulo`. Escrito distinto de cada lado,
 * un pedido largo se leería recortado en una pantalla y completo en la otra. */
const comoPedido = (f) => ({
  ...f,
  texto: f.detalle || f.titulo || "",
  quien_nombre: f.abierto_por_nombre,
});

export async function traerPedidos() {
  const { data, error } = await supabase
    .from("tickets")
    .select(CAMPOS)
    .eq("area", AREA)
    .order("id", { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []).map(comoPedido);
}

/* Mover el estado va por `mover_ticket` y NO por un `update` directo.
 *
 * Dos motivos, y el primero solo ya alcanza: `tickets` tiene RLS de lectura y
 * ninguna política de escritura, así que un `.update()` desde el navegador no
 * escribe nada — y PostgREST contesta 200 con cero filas, o sea que la pantalla
 * diría «listo» sin haber movido nada. Es el 200-que-no-es-un-éxito de
 * `/api/sync-docs`.
 *
 * Y el segundo: la función asienta el cambio en el hilo del ticket y mueve
 * `movido_en` y `cerrado_en` en el mismo paso. Un ticket es una máquina de
 * estados, no una fila que se edita — la misma razón por la que la ticketera
 * del taller no tiene un PATCH genérico.
 *
 * NO HAY BORRAR: descartar deja la fila con su historia, que es lo que dentro
 * de un año explica por qué eso no se hizo. */
export async function cambiarEstado(id, estado) {
  const { error } = await supabase.rpc("mover_ticket", {
    p_ticket: id,
    p_estado: estado,
  });
  if (error) throw new Error(error.message);
}

/* Cuántos esperan. Lo usa la tarjeta de la home. */
export async function cuantosAbiertos() {
  const { count, error } = await supabase
    .from("tickets")
    .select("id", { count: "exact", head: true })
    .eq("area", AREA)
    .in("estado", ABIERTOS);
  /* `null` es «no pude fijarme», que NO es cero: un cero dibuja la tarjeta en
   * silencio y hace creer que no hay nada pendiente. */
  if (error) return null;
  return count ?? null;
}
